import { DEFAULT_BLUR_PX, DEFAULT_BLUR_TRANSITION, MASK_FADE_GRACE_MS, } from './overlay';
export const SNAPSHOT_MASK_ATTR = 'data-ts-snapshot-mask';
export const isSnapshotMaskNode = (el) => !!el && el.hasAttribute(SNAPSHOT_MASK_ATTR);
export const firstNonMaskChild = (mount) => {
    for (const child of Array.from(mount.children)) {
        if (!isSnapshotMaskNode(child))
            return child;
    }
    return null;
};
export const DEFAULT_SCALE_TRANSITION = {
    durationMs: 80,
    easing: 'ease-in',
    disabled: false,
};
const copyCanvasPixels = (source, clone) => {
    const srcCanvases = source.querySelectorAll('canvas');
    const dstCanvases = clone.querySelectorAll('canvas');
    srcCanvases.forEach((src, i) => {
        const dst = dstCanvases[i];
        if (!dst)
            return;
        dst.width = src.width;
        dst.height = src.height;
        try {
            dst.getContext('2d')?.drawImage(src, 0, 0);
        }
        catch {
        }
    });
};
const matchScrollables = (scope, selectors) => {
    const selector = selectors.join(',');
    const els = scope.matches(selector) ? [scope] : [];
    scope.querySelectorAll(selector).forEach(el => els.push(el));
    return els;
};
const captureScrollPositions = (source, selectors) => {
    if (!selectors.length)
        return [];
    const entries = [];
    matchScrollables(source, selectors).forEach((el, index) => {
        if (el.scrollTop || el.scrollLeft) {
            entries.push({ index, top: el.scrollTop, left: el.scrollLeft });
        }
    });
    return entries;
};
const applyScrollPositions = (clone, selectors, entries) => {
    if (!entries.length)
        return;
    const all = matchScrollables(clone, selectors);
    entries.forEach(({ index, top, left }) => {
        const el = all[index];
        if (!el)
            return;
        el.scrollTop = top;
        el.scrollLeft = left;
    });
};
const URL_REF_ATTRS = [
    'clip-path',
    'mask',
    'filter',
    'fill',
    'stroke',
    'marker-start',
    'marker-mid',
    'marker-end',
];
let cloneSequence = 0;
const localizeIds = (clone) => {
    const suffix = `-tssnap${++cloneSequence}`;
    const renamed = new Map();
    const all = [clone, ...clone.querySelectorAll('*')];
    all.forEach(el => {
        const id = el.getAttribute('id');
        if (id) {
            el.setAttribute('id', id + suffix);
            renamed.set(id, id + suffix);
        }
    });
    if (renamed.size === 0)
        return;
    const rewrite = (value) => value.replace(/url\((["']?)#([^"')]+)\1\)/g, (match, quote, id) => renamed.has(id)
        ? `url(${quote}#${renamed.get(id)}${quote})`
        : match);
    all.forEach(el => {
        URL_REF_ATTRS.forEach(attr => {
            const value = el.getAttribute(attr);
            if (value && value.includes('url('))
                el.setAttribute(attr, rewrite(value));
        });
        const style = el.getAttribute('style');
        if (style && style.includes('url('))
            el.setAttribute('style', rewrite(style));
        const hrefAttr = el.hasAttribute('href')
            ? 'href'
            : el.hasAttribute('xlink:href')
                ? 'xlink:href'
                : null;
        if (hrefAttr) {
            const href = el.getAttribute(hrefAttr);
            if (href.startsWith('#') && renamed.has(href.slice(1))) {
                el.setAttribute(hrefAttr, `#${renamed.get(href.slice(1))}`);
            }
        }
    });
};
const resolveOpaqueBackground = (el) => {
    const isTransparent = (color) => color === 'transparent' || /^rgba\(.+,\s*0\)$/.test(color);
    let node = el;
    while (node) {
        const color = getComputedStyle(node).backgroundColor;
        if (color && !isTransparent(color))
            return color;
        node = node.parentElement;
    }
    return '#fff';
};
export function prepareScaledClone(root, dims) {
    const clone = root.cloneNode(true);
    localizeIds(clone);
    copyCanvasPixels(root, clone);
    const rootRect = root.getBoundingClientRect();
    clone.style.width = `${rootRect.width}px`;
    clone.style.height = `${rootRect.height}px`;
    clone.style.visibility = 'visible';
    return { clone, dims: { ...dims } };
}
export function coverWithScaledClone(mount, root, captured, transition = {}, blur = {}, scrollableSelectors = [], prepared = null) {
    const scaleTransition = {
        ...DEFAULT_SCALE_TRANSITION,
        ...transition,
    };
    const blurTransition = {
        ...DEFAULT_BLUR_TRANSITION,
        ...blur,
    };
    const prevMountPosition = mount.style.position;
    if (getComputedStyle(mount).position === 'static') {
        mount.style.position = 'relative';
    }
    const background = resolveOpaqueBackground(mount);
    const mountScrollTop = mount.scrollTop;
    const mountScrollLeft = mount.scrollLeft;
    const backdrop = document.createElement('div');
    backdrop.setAttribute(SNAPSHOT_MASK_ATTR, '');
    backdrop.style.position = 'absolute';
    backdrop.style.top = `${mountScrollTop}px`;
    backdrop.style.left = `${mountScrollLeft}px`;
    backdrop.style.width = '100%';
    backdrop.style.height = '100%';
    backdrop.style.background = background;
    backdrop.style.zIndex = '1';
    backdrop.style.pointerEvents = 'none';
    const wrapper = document.createElement('div');
    wrapper.setAttribute(SNAPSHOT_MASK_ATTR, '');
    wrapper.style.position = 'absolute';
    wrapper.style.top = `${mountScrollTop}px`;
    wrapper.style.left = `${mountScrollLeft}px`;
    wrapper.style.width = `${captured.width}px`;
    wrapper.style.height = `${captured.height}px`;
    wrapper.style.overflow = 'hidden';
    wrapper.style.transformOrigin = '0 0';
    let blurPx = 0;
    const setWrapperTransition = (ease) => {
        const transformPart = scaleTransition.disabled
            ? ''
            : `transform ${scaleTransition.durationMs}ms ${scaleTransition.easing}, `;
        wrapper.style.transition = `${transformPart}filter ${blurTransition.durationMs}ms ${ease}`;
    };
    const applyScale = (dims) => {
        wrapper.style.transform = `scale(${dims.width /
            captured.width}, ${dims.height / captured.height})`;
    };
    wrapper.style.willChange = 'transform';
    wrapper.style.zIndex = '2';
    wrapper.style.pointerEvents = 'none';
    wrapper.style.background = background;
    const scrollEntries = captureScrollPositions(root, scrollableSelectors);
    const clone = prepared
        ? prepared.clone
        : prepareScaledClone(root, captured).clone;
    if (prepared)
        copyCanvasPixels(root, clone);
    if (mountScrollTop || mountScrollLeft) {
        clone.style.marginTop = `${-mountScrollTop}px`;
        clone.style.marginLeft = `${-mountScrollLeft}px`;
    }
    wrapper.appendChild(clone);
    const mountRect = mount.getBoundingClientRect();
    if (mountRect.width > 0 && mountRect.height > 0) {
        applyScale({ width: mountRect.width, height: mountRect.height });
    }
    const prevRootVisibility = root.style.visibility;
    root.style.visibility = 'hidden';
    mount.appendChild(backdrop);
    mount.appendChild(wrapper);
    void wrapper.offsetWidth;
    applyScrollPositions(clone, scrollableSelectors, scrollEntries);
    setWrapperTransition(blurTransition.easeIn);
    return {
        scaleTo(dims) {
            applyScale(dims);
        },
        swapTo() {
        },
        setBlur(px = DEFAULT_BLUR_PX) {
            setWrapperTransition(px >= blurPx ? blurTransition.easeIn : blurTransition.easeOut);
            wrapper.style.filter = px > 0 ? `blur(${px}px)` : '';
            blurPx = px;
        },
        release() {
            backdrop.remove();
            wrapper.remove();
            handBackRoot();
            mount.style.position = prevMountPosition;
        },
        dismiss(fade) {
            handBackRoot();
            let done = false;
            const finish = () => {
                if (done)
                    return;
                done = true;
                clearTimeout(timer);
                wrapper.removeEventListener('transitionend', onEnd);
                backdrop.remove();
                wrapper.remove();
                mount.style.position = prevMountPosition;
            };
            const onEnd = (event) => {
                if (event.target === wrapper &&
                    event.propertyName === 'opacity')
                    finish();
            };
            wrapper.style.transition = `opacity ${fade.durationMs}ms ${fade.easing}`;
            backdrop.style.transition = `opacity ${fade.durationMs}ms ${fade.easing}`;
            wrapper.style.opacity = '0';
            backdrop.style.opacity = '0';
            wrapper.addEventListener('transitionend', onEnd);
            const timer = setTimeout(finish, fade.durationMs + MASK_FADE_GRACE_MS);
        },
    };
    function handBackRoot() {
        root.style.visibility = prevRootVisibility;
        const current = firstNonMaskChild(mount);
        if (current &&
            current !== root &&
            current.style.visibility === 'hidden') {
            current.style.visibility = '';
        }
    }
}
//# sourceMappingURL=scale-overlay.js.map